import { useState, useEffect, useRef } from 'react';

export default function CsvImport({ onImported }) {
  const [status, setStatus]     = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult]     = useState(null);
  const inputRef = useRef();

  useEffect(() => {
    fetch('/api/accounts/import-status')
      .then(r => r.json())
      .then(setStatus)
      .catch(() => setStatus({ imported: false }));
  }, []);

  // Compresses the CSV in the browser before sending. Account CSVs are highly
  // repetitive and compress about 4x (3.75MB → 0.95MB at 25,700 accounts),
  // which matters because the host caps a request body at 4.5MB — at ~153 bytes
  // a row an uncompressed file hits that wall at roughly 30,000 accounts.
  //
  // Sent as application/gzip rather than with a Content-Encoding header, so no
  // proxy in between decides to "helpfully" decompress or reject it; the server
  // unzips that content type explicitly.
  //
  // Returns null when the browser has no CompressionStream (Safari before
  // 16.4), so the caller falls back to sending the text as-is.
  async function gzip(text) {
    if (typeof CompressionStream !== 'function') return null;
    try {
      const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
      return await new Response(stream).blob();
    } catch {
      return null;
    }
  }

  async function uploadFile(file) {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const text = await file.text();
      if (!text.trim()) throw new Error('That file is empty.');

      const packed = await gzip(text);
      const rawMb = text.length / 1048576;

      // Without CompressionStream the raw file goes up as-is, and the host
      // rejects a body over 4.5MB — often by dropping the connection rather
      // than answering, which surfaces as an unexplained network failure.
      // Say so up front instead of letting it fail confusingly.
      if (!packed && rawMb > 4) {
        throw new Error(
          `This browser cannot compress the upload, and the file is ${rawMb.toFixed(1)}MB — ` +
          `over the 4.5MB the server accepts uncompressed. Use a current version of ` +
          `Chrome, Edge, Firefox or Safari 16.4+, or split the CSV in half.`
        );
      }

      // One automatic retry. A large upload from a phone drops often enough
      // that a single retry turns most failures into successes, and a retry is
      // safe here: the import replaces the account list wholesale rather than
      // appending, so sending it twice lands the same result as sending it once.
      const started = Date.now();
      let res, netErr;
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          res = await fetch('/api/accounts/import', {
            method: 'POST',
            headers: { 'Content-Type': packed ? 'application/gzip' : 'text/plain' },
            body: packed || text,
          });
          netErr = null;
          break;
        } catch (err) {
          netErr = err;
          if (attempt === 2) break;
          await new Promise(r => setTimeout(r, 1500));
        }
      }
      if (netErr) {
        const secs = Math.round((Date.now() - started) / 1000);
        const size = packed
          ? `${(packed.size / 1048576).toFixed(1)}MB compressed from ${rawMb.toFixed(1)}MB`
          : `${rawMb.toFixed(1)}MB uncompressed`;
        // Deliberately does NOT say "check your connection". The request is far
        // more likely to have been cut off mid-flight than never to have left,
        // and blaming the network sends people looking in the wrong place.
        throw new Error(
          `The upload did not complete — the connection closed after ${secs}s ` +
          `while sending ${size}. It was retried once. If this keeps happening on ` +
          `mobile, try again on a desktop or a stronger connection.`
        );
      }

      // A failed upload rarely returns JSON — a size limit or a gateway error
      // comes back as HTML or plain text, and calling res.json() on that threw
      // a raw browser parse error with no status code, which is unreadable and
      // hides the actual cause. Read the status first.
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        if (res.status === 413) {
          const mb = (text.length / 1048576).toFixed(1);
          throw new Error(
            `File too large for the server (${mb}MB, even compressed). ` +
            `Split the CSV in half and import each part.`
          );
        }
        const snippet = detail.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 140);
        throw new Error(`Upload failed — server returned ${res.status}${snippet ? `: ${snippet}` : ''}`);
      }

      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      // An import that was not persisted is not an import. On the hosted app the
      // parsed accounts land in ephemeral per-instance storage and are gone at
      // the next cold start, so reporting a plain success for one is how an
      // upload appears to work and then quietly reverts.
      setResult({ ok: true, count: data.count, persisted: data.persisted !== false, persistError: data.persistError });
      const s = await fetch('/api/accounts/import-status').then(r => r.json());
      setStatus(s);
      if (onImported) onImported(data.count);
    } catch (e) {
      // A bare browser exception ("The string did not match the expected
      // pattern", "Load failed") says nothing about what went wrong. Say what
      // was being attempted alongside it.
      setResult({ ok: false, error: e && e.message ? e.message : String(e) });
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }

  if (status === null) return null;

  return (
    <div style={{ marginBottom: 18 }}>
      {status.imported ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          background: 'rgba(16,185,129,0.1)',
          border: '1px solid rgba(16,185,129,0.2)',
          borderRadius: 10, padding: '10px 16px', fontSize: 13,
        }}>
          <span style={{ color: '#34d399', fontWeight: 700, fontSize: 14 }}>✓</span>
          <span style={{ color: '#6ee7b7', fontWeight: 600 }}>
            {status.count.toLocaleString()} accounts loaded
          </span>
          <span style={{ color: '#475569', fontSize: 12 }}>
            {status.domains.map(d => `${d.count.toLocaleString()} ${d.domain}`).join(' · ')}
          </span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {status.withRegion > 0 && (
              <span style={{
                fontSize: 11, background: 'rgba(96,165,250,0.15)', color: '#60a5fa',
                borderRadius: 20, padding: '2px 9px', fontWeight: 600,
              }}>
                🌍 {status.withRegion.toLocaleString()} with region
              </span>
            )}
            {status.withSmsCountry > 0 && (
              <span style={{
                fontSize: 11, background: 'rgba(167,139,250,0.15)', color: '#a78bfa',
                borderRadius: 20, padding: '2px 9px', fontWeight: 600,
              }}>
                📱 {status.withSmsCountry.toLocaleString()} with SMS
              </span>
            )}
          </div>
          <span style={{ color: '#334155', fontSize: 11, marginLeft: 'auto' }}>
            {new Date(status.importedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          </span>
          <button
            onClick={() => inputRef.current?.click()}
            style={{
              fontSize: 11, color: '#64748b', background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7,
              padding: '4px 11px', cursor: 'pointer', fontWeight: 500,
            }}
          >
            Replace CSV
          </button>
          <input ref={inputRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={e => uploadFile(e.target.files[0])} />
        </div>
      ) : (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#6366f1' : 'rgba(255,255,255,0.12)'}`,
            borderRadius: 12,
            padding: '22px 28px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.03)',
            transition: 'all 0.15s',
          }}
        >
          <input ref={inputRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={e => uploadFile(e.target.files[0])} />
          <div style={{ fontSize: 24, marginBottom: 8 }}>📂</div>
          <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>
            {uploading
              ? <><span style={{ display: 'inline-block', animation: 'spin 0.8s linear infinite' }}>↻</span> Importing…</>
              : 'Drop your accounts CSV here, or click to browse'}
          </div>
          <div style={{ fontSize: 12, color: '#475569', marginTop: 5 }}>
            Expects columns: <code style={{ background: 'rgba(255,255,255,0.07)', color: '#94a3b8', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>email, …, country_iso, phone_iso</code>
          </div>
        </div>
      )}

      {result && (
        <div style={{
          marginTop: 8, padding: '8px 14px', borderRadius: 8, fontSize: 13,
          background: !result.ok ? 'rgba(220,38,38,0.12)' : result.persisted ? 'rgba(16,185,129,0.12)' : 'rgba(251,146,60,0.14)',
          color:      !result.ok ? '#f87171'              : result.persisted ? '#6ee7b7'              : '#fdba74',
          border: `1px solid ${!result.ok ? 'rgba(220,38,38,0.25)' : result.persisted ? 'rgba(16,185,129,0.25)' : 'rgba(251,146,60,0.3)'}`,
          fontWeight: 500,
        }}>
          {!result.ok
            ? `✗ ${result.error}`
            : result.persisted
              ? `✓ ${result.count.toLocaleString()} accounts imported successfully`
              : <>
                  ⚠ {result.count.toLocaleString()} accounts were read, but could NOT be saved
                  {result.persistError ? ` — ${result.persistError}` : ''}.
                  <div style={{ marginTop: 4, fontWeight: 400 }}>
                    They will disappear when the server restarts. Nothing has been lost from the
                    previous import. Fix the cause and upload again.
                  </div>
                </>}
        </div>
      )}
    </div>
  );
}
